import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2,
  ArrowLeft, Shield, Sparkles, RefreshCw, Copy, Check
} from 'lucide-react';
import { registration } from '../../services/api/client';
import { useI18n } from '../../i18n';
import fr from '../../i18n/locales/fr';

// Helper to get French translations (always French on reset password page for this French platform)
const getFrTranslation = (key, params = {}) => {
  const keys = key.split('.');
  let value = fr;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  if (typeof value !== 'string') return key;
  return value.replace(/\{(\w+)\}/g, (match, param) => {
    return params[param] !== undefined ? params[param] : match;
  });
};

// Password Generator Component
const PasswordGenerator = ({ onSelect }) => {
  // Use French translations for consistency with other auth pages
  const t = getFrTranslation;
  const [passwords, setPasswords] = useState({ classic: '', quantum: '' });
  const [copied, setCopied] = useState({ classic: false, quantum: false });
  const [selectedType, setSelectedType] = useState(null);

  const generateClassicPassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let password = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    const ensureChars = [
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)],
      'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)],
      '0123456789'[Math.floor(Math.random() * 10)],
      '!@#$%^&*'[Math.floor(Math.random() * 8)]
    ];
    return ensureChars.join('') + password.slice(4);
  };

  const generateQuantumPassword = () => {
    const length = 32;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?~`';
    let password = '';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }
    return password;
  };

  const regenerate = () => {
    setPasswords({
      classic: generateClassicPassword(),
      quantum: generateQuantumPassword()
    });
    setCopied({ classic: false, quantum: false });
    setSelectedType(null);
  };

  useEffect(() => {
    regenerate();
  }, []);

  const copyToClipboard = async (type) => {
    await navigator.clipboard.writeText(passwords[type]);
    setCopied({ ...copied, [type]: true });
    setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
  };

  const selectPassword = (type) => {
    setSelectedType(type);
    onSelect(passwords[type]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-300">{t('auth.passwordGenerator')}</h4>
        <button
          type="button"
          onClick={regenerate}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          {t('auth.regenerate')}
        </button>
      </div>

      {/* Classic Password */}
      <div
        onClick={() => selectPassword('classic')}
        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selectedType === 'classic'
            ? 'border-brand-green bg-brand-green/10'
            : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-white">{t('auth.classic')}</span>
            <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded">{t('auth.charactersCount', { count: 16 })}</span>
          </div>
          {selectedType === 'classic' && <Check className="w-5 h-5 text-brand-green" />}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 p-2 bg-gray-900 rounded text-sm text-gray-300 font-mono truncate">
            {passwords.classic}
          </code>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyToClipboard('classic'); }}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            {copied.classic ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4 text-gray-400" />}
          </button>
        </div>
      </div>

      {/* Quantum-Resistant Password */}
      <div
        onClick={() => selectPassword('quantum')}
        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selectedType === 'quantum'
            ? 'border-brand-green bg-brand-green/10'
            : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-white">{t('auth.quantumResistant')}</span>
            <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">{t('auth.charactersCount', { count: 32 })}</span>
          </div>
          {selectedType === 'quantum' && <Check className="w-5 h-5 text-brand-green" />}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 p-2 bg-gray-900 rounded text-xs text-gray-300 font-mono truncate">
            {passwords.quantum}
          </code>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyToClipboard('quantum'); }}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            {copied.quantum ? <Check className="w-4 h-4 text-brand-green" /> : <Copy className="w-4 h-4 text-gray-400" />}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('auth.quantumSecurityDescription')}
        </p>
      </div>
    </div>
  );
};

const ResetPassword = () => {
  // Use French translations for consistency with other auth pages (French platform)
  const t = getFrTranslation;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    showPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordValidation = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password),
      passwordsMatch: password === formData.confirmPassword && password.length > 0
    };
  }, [formData.password, formData.confirmPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(v => v);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handlePasswordSelect = (password) => {
    setFormData(prev => ({
      ...prev,
      password,
      confirmPassword: password
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError(t('errors.passwordCriteriaNotMet'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await registration.resetPassword(token, formData.password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login?reset=success'), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('errors.resetPasswordFailed');
      if (errorMessage.includes('expired') || errorMessage.includes('invalide')) {
        setError(t('errors.resetLinkExpired'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111] border border-gray-800 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-brand-green" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('auth.passwordChanged')}
          </h1>
          <p className="text-gray-400 mb-6">
            {t('auth.passwordResetSuccess')}
          </p>
          <div className="flex items-center justify-center gap-2 text-brand-green">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('common.redirecting')}</span>
          </div>
        </div>
      </div>
    );
  }

  // No token state - show friendly info message instead of error
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#111] border border-gray-800 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-cyan-500/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{t('auth.resetPassword')}</h2>
          <p className="text-gray-400 mb-6">
            {t('auth.resetPasswordNeedsLink')}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/logo.png"
              alt="SYLINK"
              className="h-12 w-auto mx-auto mb-6"
            />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('auth.resetPassword')}
          </h1>
          <p className="text-gray-400">
            {t('auth.chooseSecurePassword')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Password Generator */}
          <PasswordGenerator onSelect={handlePasswordSelect} />

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-gray-700" />
            <span className="text-sm text-gray-500">{t('auth.orEnterManually')}</span>
            <div className="flex-1 h-px bg-gray-700" />
          </div>

          {/* Manual Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.newPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={formData.showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl pl-12 pr-12 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                  placeholder={t('auth.newPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {formData.showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={formData.showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full bg-[#0a0a0a] border rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    formData.confirmPassword
                      ? passwordValidation.passwordsMatch
                        ? 'border-brand-green/50 focus:ring-brand-green/20'
                        : 'border-red-500/50 focus:ring-red-500/20'
                      : 'border-gray-700 focus:border-brand-green/50 focus:ring-brand-green/20'
                  }`}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                />
              </div>
            </div>

            {/* Password Requirements */}
            {formData.password && (
              <div className="grid grid-cols-2 gap-2 text-xs p-4 bg-gray-900/50 rounded-xl">
                <div className={`flex items-center gap-1 ${passwordValidation.minLength ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.minChars')}
                </div>
                <div className={`flex items-center gap-1 ${passwordValidation.hasUppercase ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.uppercase')}
                </div>
                <div className={`flex items-center gap-1 ${passwordValidation.hasLowercase ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.lowercase')}
                </div>
                <div className={`flex items-center gap-1 ${passwordValidation.hasNumber ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.number')}
                </div>
                <div className={`flex items-center gap-1 ${passwordValidation.hasSpecial ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.specialChar')}
                </div>
                <div className={`flex items-center gap-1 ${passwordValidation.passwordsMatch ? 'text-brand-green' : 'text-gray-500'}`}>
                  <CheckCircle className="w-3 h-3" /> {t('auth.passwordsMatch')}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !isPasswordValid}
              className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.resettingPassword')}
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  {t('auth.resetPassword')}
                </>
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
